import { CardTypeCallToActions, Learning_Type } from '@app/enums/_index';
import { Pipe, PipeTransform } from '@angular/core';
import {
  getEnrollmentStatus,
  isDigitalSubscriptionEnrolled,
  isValidSubscription,
} from '@app/utilities/_index';

import { User } from '@app/models/_index';
import { environment } from '@environments/environment';

@Pipe({
  name: 'cardTypeCallToActions',
  pure: false,
  standalone: false,
})
export class CardTypeCallToActionsPipe implements PipeTransform {
  /**
   * Return transformed learning type string to new display call to action for unsigned
   * or user that is not logged in
   * NOTE: Pipe must be impure due to the aynchronous nature of the user service which contains the user subscription and enrollments.
   * @param learningType - learning type to transform to new display type
   * @param user - If user is signed in, then the transformed type will return a different display type.
   * @param subscriptionId - Subscription id used to check if the user has this subscription
   * @param relatedSubscriptionIds - Related subscriptions used to check if the course can appear in 1 to N subscriptions
   * @param courseId - Optional. Course id used to check if the use has this enrollment already to determine enrollment status
   * @param isFreeCourse - Optional. Is Free course if the course IsFreeCourse additional field from course is true, else false.
   * @param relatedCourseIds - Optional. Related course ids used to check for conslidated view enrollment options to show an arrow with call to action
   * @param view - Optional. Determines your current view, in case user signs in and wants to redirect back to this view.
   * @returns - transformed call to action string
   */
  public transform(
    learningType: string,
    user: User,
    subscriptionId: number,
    relatedSubscriptionIds: number[],
    courseId?: number,
    isFreeCourse?: boolean,
    relatedCourseIds?: number[],
    view?: string
  ): string {
    switch (learningType) {
      case Learning_Type.SUBSCRIPTION:
        return CardTypeCallToActions.LEARN_MORE;
      case Learning_Type.E_LEARNING:
      case Learning_Type.QUICK_LEARNING:
      case Learning_Type.WEBINAR:
        if (user) {
          if (user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            const isSubscribed = isValidSubscription(relatedSubscriptionIds, subscriptionId, user);
            if (isSubscribed || isFreeCourse) {
              return getEnrollmentStatus(user, courseId, isFreeCourse, isSubscribed, learningType);
            } else {
              if (view && view === 'detail') {
                return CardTypeCallToActions.LEARN_MORE;
              } else {
                return CardTypeCallToActions.SUBSCRIPTION_REQUIRED;
              }
            }
          } else {
            if (isFreeCourse) {
              return getEnrollmentStatus(user, courseId, isFreeCourse, false, learningType);
            } else {
              if (view && view === 'detail') {
                return CardTypeCallToActions.LEARN_MORE;
              } else {
                return CardTypeCallToActions.SUBSCRIPTION_REQUIRED;
              }
            }
          }
        } else {
          return CardTypeCallToActions.SIGN_IN;
        }
      case Learning_Type.CERTIFICATION_EXAM:
        if (user) {
          if (user && user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            const isSubscribed = isValidSubscription(relatedSubscriptionIds, subscriptionId, user);
            return getEnrollmentStatus(user, courseId, isFreeCourse, isSubscribed, learningType);
          } else {
            // Enable Enroll option for Certification Exam Genesys Cloud CX: Professional Certification and Genesys Cloud CX: Developer Certification if the user is enrolled to Genesys Cloud CX Partner Digital Learning or Genesys Cloud CX Partner Digital Plus Learning
            const isSubscribed = isDigitalSubscriptionEnrolled(courseId, user);
            return getEnrollmentStatus(user, courseId, isFreeCourse, isSubscribed, learningType);
          }
        } else {
          return CardTypeCallToActions.SIGN_IN;
        }
      case Learning_Type.SELF_STUDY:
      case Learning_Type.INSTRUCTOR_LED:
      case Learning_Type.INSTRUCTOR_LED_IN_PERSON:
      case Learning_Type.INSTRUCTOR_LED_VIRTUAL:
      case Learning_Type.VIRTUAL:
      case Learning_Type.IN_PERSON:
      case Learning_Type.WORKSHOP:
        // NOTE: Show view enrollment options regardless if user is signed in or not.
        if (relatedCourseIds && relatedCourseIds.length > 0) {
          return CardTypeCallToActions.VIEW_ENROLLMENT_MULTIPLE_OPTIONS;
        } else {
          return CardTypeCallToActions.VIEW_ENROLLMENT_OPTIONS;
        }
      default:
        return CardTypeCallToActions.VIEW_ENROLLMENT_OPTIONS;
    }
  }
}
